import {
  bare,
  broadcastIn,
  endsInAQuestion,
  fileVerbIn,
  isAnAck,
  mentionsIn,
  names,
  talkedAbout,
  taskVerbIn,
  toolish,
  vocativeIn,
  whatIsSaid,
} from "./addressing.ts";

export type Turn = { handle: string; isAgent: boolean; text: string };

export type ReplyTo = { handle: string | null; isAgent: boolean };

export type Room = {
  sender: string;

  text: string;

  people: readonly string[];

  agents: readonly string[];

  history: readonly Turn[];
  replyTo?: ReplyTo | null;
};

type Facts = {
  kind: "person" | "agent";
  mentioned: boolean;
  named: boolean;
  talkedAbout: boolean;
  otherNamed: boolean;
  otherPersonNamed: boolean;
  vocativePresent: boolean;
  vocativeAndOnlyOne: boolean;
  vocativeAmbiguous: boolean;
  replyToThem: boolean;
  replyToAnAgent: boolean;
  replyToAPerson: boolean;
  theLastPerson: boolean;
  turnsSince: number;
  endedInAQuestion: boolean;
  theOnlyAgent: boolean;
  manyAgents: boolean;
  alone: boolean;
  anAck: boolean;
  broadcast: boolean;
  taskVerb: boolean;
  fileVerb: boolean;
  somebodyElseIsOwedAnAnswer: boolean;

  continuationOwned: boolean;
  toolish: boolean;
};

const EVIDENCE = {
  mentioned: 0.97,
  namedPlainly: 0.9,
  replyToThem: 0.95,
  replyToTheOnlyAgent: 0.92,
  replyToSomeAgent: 0.5,
  replyToTheLastPerson: 0.88,
  replyToSomePerson: 0.35,
  vocativeAndOnlyOne: 0.85,
  vocativeAmbiguous: 0.4,
  theOnlyAgentHere: 0.8,
  spokeLast: 0.7,
  spokeLastWhileAnotherIsOwed: 0.4,
  askedAndWaiting: 0.72,
  spokeRecently: 0.45,
  personSpokeLast: 0.45,
  broadcastToAPerson: 0.4,
  broadcastToAnAgent: 0.2,
  taskForTheOnlyAgent: 0.62,
  taskForTheFittingAgent: 0.8,
  taskForTheOtherAgent: 0.3,
  nothingAtAll: 0.05,
} as const;

const DAMP = {
  anotherPersonNamed: 0.35,
  anotherNamed: 0.35,
  vocativeCeiling: 0.35,
  replyToAPerson: 0.3,
  replyToAnAgent: 0.35,
  talkedAbout: 0.25,
  anAck: 0.2,
} as const;

const AT_MOST_CERTAIN = 0.99;

const RECENTLY = 2;

const STILL_OWED = 3;

export const LIKELY = 0.5;

export const AT_MOST = 2;

export function candidatesIn(room: Room): { handle: string; kind: "person" | "agent" }[] {
  return [
    ...room.people
      .filter((one) => one !== room.sender)
      .map((one) => ({ handle: one, kind: "person" as const })),
    ...room.agents
      .filter((one) => one !== room.sender)
      .map((one) => ({ handle: one, kind: "agent" as const })),
  ];
}

function factsIn(room: Room): Map<string, Facts> {
  const said = bare(whatIsSaid(room.text).toLowerCase());
  const mentioned = mentionsIn(room.text);
  const here = candidatesIn(room);
  const people = room.people.filter((one) => one !== room.sender);

  const since = new Map<string, number>();
  const endedInAQuestion = new Map<string, boolean>();
  room.history.forEach((turn, at) => {
    since.set(turn.handle, room.history.length - 1 - at);
    endedInAQuestion.set(turn.handle, endsInAQuestion(turn.text));
  });
  const lastPerson = [...room.history].reverse().find((turn) => !turn.isAgent)?.handle ?? null;
  const lastTurn = room.history.at(-1);
  const priorTurn = room.history.at(-2);

  const owed =
    [...room.history.slice(-STILL_OWED)].reverse().find((turn) => endsInAQuestion(turn.text))?.handle ?? null;

  const vocative = vocativeIn(said);
  const anAck = isAnAck(room.text);
  const broadcast = broadcastIn(said);
  const taskVerb = taskVerbIn(said);
  const fileVerb = fileVerbIn(said);

  const spelled = new Map<string, { named: boolean; about: boolean }>();
  for (const one of here) {
    const name = bare(one.handle.toLowerCase());
    spelled.set(one.handle, {
      named: mentioned.has(one.handle.toLowerCase()) || names(said, name),
      about: talkedAbout(said, name),
    });
  }

  const addressedByName = (handle: string): boolean => {
    const how = spelled.get(handle);
    return Boolean(how?.named && !how.about);
  };

  const facts = new Map<string, Facts>();
  for (const one of here) {
    const how = spelled.get(one.handle) ?? { named: false, about: false };
    const isAgent = one.kind === "agent";
    facts.set(one.handle, {
      kind: one.kind,
      mentioned: mentioned.has(one.handle.toLowerCase()),
      named: how.named,
      talkedAbout: how.about,
      otherNamed: here.some((other) => other.handle !== one.handle && addressedByName(other.handle)),
      otherPersonNamed:
        people.some((other) => other !== one.handle && addressedByName(other)) &&
        !addressedByName(one.handle),
      vocativePresent: vocative,
      vocativeAndOnlyOne: vocative && !isAgent && people.length === 1 && people[0] === one.handle,
      vocativeAmbiguous: vocative && !isAgent && people.length > 1,
      replyToThem: (room.replyTo?.handle ?? null) === one.handle,
      replyToAnAgent: room.replyTo?.isAgent === true,
      replyToAPerson: room.replyTo !== null && room.replyTo !== undefined && !room.replyTo.isAgent,
      theLastPerson: lastPerson === one.handle,
      turnsSince: since.get(one.handle) ?? Number.POSITIVE_INFINITY,
      endedInAQuestion: endedInAQuestion.get(one.handle) ?? false,
      theOnlyAgent: isAgent && room.agents.length === 1,
      manyAgents: room.agents.length > 1,
      alone: room.people.length === 1 && room.agents.length === 1,
      anAck,
      broadcast,
      taskVerb,
      fileVerb,
      somebodyElseIsOwedAnAnswer: owed !== null && owed !== one.handle,
      continuationOwned:
        isAgent &&
        lastTurn?.handle === one.handle &&
        lastTurn.isAgent &&
        priorTurn?.handle === room.sender &&
        !priorTurn.isAgent,
      toolish: toolish(one.handle),
    });
  }
  return facts;
}

function evidenceFor(facts: Facts): number[] {
  const found: number[] = [];
  if (facts.mentioned) found.push(EVIDENCE.mentioned);
  else if (facts.named && !facts.talkedAbout) found.push(EVIDENCE.namedPlainly);
  if (facts.replyToThem) found.push(EVIDENCE.replyToThem);
  if (facts.replyToAnAgent && facts.kind === "agent") {
    found.push(facts.theOnlyAgent ? EVIDENCE.replyToTheOnlyAgent : EVIDENCE.replyToSomeAgent);
  }
  if (facts.replyToAPerson && facts.kind === "person") {
    found.push(facts.theLastPerson ? EVIDENCE.replyToTheLastPerson : EVIDENCE.replyToSomePerson);
  }
  if (facts.vocativeAndOnlyOne) found.push(EVIDENCE.vocativeAndOnlyOne);
  if (facts.vocativeAmbiguous) found.push(EVIDENCE.vocativeAmbiguous);
  if (facts.kind === "agent") {
    if (facts.alone) found.push(EVIDENCE.theOnlyAgentHere);
    if (
      facts.turnsSince === 0 &&
      (facts.alone ||
        (facts.continuationOwned && !facts.manyAgents) ||
        (facts.manyAgents && !facts.continuationOwned))
    ) {
      found.push(
        facts.somebodyElseIsOwedAnAnswer ? EVIDENCE.spokeLastWhileAnotherIsOwed : EVIDENCE.spokeLast,
      );
    }
    if (facts.endedInAQuestion && facts.turnsSince <= RECENTLY) found.push(EVIDENCE.askedAndWaiting);
    else if (facts.turnsSince <= RECENTLY) found.push(EVIDENCE.spokeRecently);
  } else if (facts.turnsSince === 0 && !facts.otherNamed) {
    found.push(EVIDENCE.personSpokeLast);
  }
  if (facts.broadcast) {
    found.push(facts.kind === "person" ? EVIDENCE.broadcastToAPerson : EVIDENCE.broadcastToAnAgent);
  }
  if (facts.kind === "agent" && facts.taskVerb && !facts.anAck) {
    if (!facts.manyAgents) found.push(EVIDENCE.taskForTheOnlyAgent);
    else
      found.push(
        facts.fileVerb === facts.toolish ? EVIDENCE.taskForTheFittingAgent : EVIDENCE.taskForTheOtherAgent,
      );
  }
  return found;
}

const spelledOut = (facts: Facts): boolean => facts.mentioned || (facts.named && !facts.talkedAbout);

function scoreOf(facts: Facts): number {
  const found = evidenceFor(facts);
  let score = found.length > 0 ? Math.max(...found) : EVIDENCE.nothingAtAll;
  if (!spelledOut(facts)) {
    if (facts.otherPersonNamed && facts.kind === "agent") score *= DAMP.anotherPersonNamed;
    else if (facts.otherNamed && facts.kind === "person") score *= DAMP.anotherNamed;
    if (facts.vocativePresent && facts.kind === "agent") score = Math.min(score, DAMP.vocativeCeiling);
    if (facts.replyToAPerson && facts.kind === "agent") score *= DAMP.replyToAPerson;
    if (facts.replyToAnAgent && facts.kind === "person") score *= DAMP.replyToAnAgent;
    if (facts.talkedAbout) score *= DAMP.talkedAbout;
  }
  if (facts.anAck && !facts.mentioned && !facts.replyToThem) score *= DAMP.anAck;
  return Math.max(0, Math.min(AT_MOST_CERTAIN, score));
}

export type Scored = { handle: string; kind: "person" | "agent"; score: number; spelled: boolean };

export function scoreEveryone(room: Room): Scored[] {
  const facts = factsIn(room);
  return [...facts.entries()]
    .map(([handle, one]) => ({ handle, kind: one.kind, score: scoreOf(one), spelled: spelledOut(one) }))
    .sort((a, b) => b.score - a.score || a.handle.localeCompare(b.handle));
}

export type Word = "typed" | "likely" | "unlikely";

export type GoesTo = { handle: string; kind: "person" | "agent"; word: Word };

export type Corrections = { dropped?: readonly string[]; added?: readonly string[] };

export function goesTo(room: Room, on: boolean, corrections: Corrections = {}): GoesTo[] {
  const scored = scoreEveryone(room);
  const dropped = new Set(corrections.dropped ?? []);
  const added = new Set(corrections.added ?? []);

  const mentioned = mentionsIn(room.text);
  const typed = scored.filter((one) => mentioned.has(one.handle.toLowerCase()));
  const spelled = new Set(typed.map((one) => one.handle));
  const guessed = scored.filter(
    (one) => on && !spelled.has(one.handle) && !dropped.has(one.handle) && one.score >= LIKELY,
  );
  let likely = guessed.slice(0, AT_MOST);
  const everybody = spelled.size + likely.length === scored.length;
  if (scored.length >= 2 && everybody && !likely.some((one) => one.spelled)) likely = [];
  const byHand = scored.filter((one) => added.has(one.handle) && !spelled.has(one.handle));
  const chosen = [...likely, ...byHand.filter((one) => !likely.some((two) => two.handle === one.handle))];
  const said = new Set([...spelled, ...chosen.map((one) => one.handle)]);
  return [
    ...typed.map((one) => ({ handle: one.handle, kind: one.kind, word: "typed" as const })),
    ...chosen.map((one) => ({ handle: one.handle, kind: one.kind, word: "likely" as const })),
    ...scored
      .filter((one) => !said.has(one.handle))
      .map((one) => ({ handle: one.handle, kind: one.kind, word: "unlikely" as const })),
  ];
}

export function withRouting(text: string, likely: readonly string[]): string {
  if (text.trim() === "") return text;
  const already = mentionsIn(text);
  const missing = likely.filter((handle) => !already.has(handle.toLowerCase()));
  return missing.length === 0 ? text : `${missing.map((handle) => `@${handle}`).join(" ")} ${text}`;
}
